import equal from 'fast-deep-equal';
import site from '../../site';
import globals from '../../globals';
import assets from '../../assets';
import customization from '../../customization';
import siteLocales from '../../site-locales';

import createAssets from './saving-action-creators/create-assets';
import createPages from './saving-action-creators/create-pages';
import createCustomColorPalettes from './saving-action-creators/create-custom-color-palettes';
import updateCustomColorPalettes from './saving-action-creators/update-custom-color-palettes';
import deleteCustomColorPalettes from './saving-action-creators/delete-custom-color-palettes';
import updateCurrentColorPaletteVersion from './saving-action-creators/update-current-color-palette-version';
import updateCurrentButtonPresetVersion from './saving-action-creators/update-current-button-preset-version';
import createCurrentButtonPresetVersion from './saving-action-creators/create-current-button-preset-version';
import createCurrentColorPaletteVersion from './saving-action-creators/create-current-color-palette-version';
import deleteAssets from './saving-action-creators/delete-assets';
import deletePages from './saving-action-creators/delete-pages';
import setSiteName from './saving-action-creators/set-site-name';
import createSiteProperties from './saving-action-creators/create-site-properties';
import updateSiteProperties from './saving-action-creators/update-site-properties';
import getAssetSourcesToDelete from './saving-action-creators/helpers/get-asset-sources-to-delete';
import updateCustomTextColors from './saving-action-creators/update-custom-text-colors';
import createCustomTextColors from './saving-action-creators/create-custom-text-colors';
import getAssetsToCreate from './saving-action-creators/helpers/get-assets-to-create';
import getAssetsToUpdate from './saving-action-creators/helpers/get-assets-to-update';
import getPageIdsToDelete from './saving-action-creators/helpers/get-page-ids-to-delete';
import getPagesToCreate from './saving-action-creators/helpers/get-pages-to-create';
import getPagesToUpdate from './saving-action-creators/helpers/get-pages-to-update';
import getSitePropertiesToCreate from './saving-action-creators/helpers/get-site-properties-to-create';
import getSitePropertiesToUpdate from './saving-action-creators/helpers/get-site-properties-to-update';
import getCustomPalettesToCreate from '../../customization/helpers/get-custom-palettes-to-create';
import getCustomPalettesToUpdate from '../../customization/helpers/get-custom-palettes-to-update';
import getCustomPalettesToDelete from '../../customization/helpers/get-custom-palettes-to-delete';
import initialData from './initial-data';
import updateActivePageId from './saving-action-creators/update-active-page-id';
import updateAssets from './saving-action-creators/update-assets';
import updatePages from './saving-action-creators/update-pages';
import updateMetaSettings from './saving-action-creators/update-meta-settings';
import updateCleanUrlEnabled from './saving-action-creators/update-clean-urls-enabled';
import updateMetaRedirects from './saving-action-creators/update-meta-redirects';
import updateMetaIntegrations from './saving-action-creators/update-meta-integrations';
import normalizeLocalizedSource from '../../site-locales/helpers/normalize-localized-source';
import updateCustomColors from './saving-action-creators/update-custom-colors';
import createCustomColors from './saving-action-creators/create-custom-colors';

export default (state) => {
  const savingActions = [];

  const initialPages = initialData.getPages();
  const currentPages = site.selectors.getPages(state);
  const initialGlobals = initialData.getGlobals();
  const currentGlobals = globals.selectors.getGlobals(state);
  const affectedMediaAssets = assets.selectors.getAffectedAssets(state);
  const currentLocale = siteLocales.selectors.getCurrentLocale(state);

  const initialSettings = initialData.getSettings();
  const currentSettings = site.selectors.getSettings(state);

  const initialActivePageId = initialData.getActivePageId();
  const currentActivePageId = site.selectors.getActivePageId(state);

  const initialProperties = initialData.getProperties();
  const currentProperties = site.selectors.getSiteProperties(state);

  const initialSiteName = initialData.getSiteName();
  const currentSiteName = site.selectors.getSiteName(state);

  const initialCleanUrlEnabled = initialData.getCleanUrlsEnabled();
  const currentCleanUrlEnabled = site.selectors.getCleanUrlsEnabled(state);

  const initialCustomColorPalettes = initialData.getCustomColorPalettes();
  const currentCustomColorPalettes = customization.selectors.getCustomColorPalettes(state);

  const initialColorPaletteVersion = initialData.getCurrentColorPaletteVersion();
  const currentColorPaletteVersion = customization.selectors.getCurrentColorPaletteVersion(state);

  const initialButtonPresetVersion = initialData.getCurrentButtonPresetVersion();
  const currentButtonPresetVersion = customization.selectors.getCurrentButtonPresetVersion(state);

  const initialCustomTextColors = initialData.getCustomTextColors();
  const currentCustomTextColors = customization.selectors.getCustomTextColors(state);

  const initialCustomColors = initialData.getCustomColors();
  const currentCustomColors = customization.selectors.getCustomColors(state);

  const initialRedirects = initialData.getRedirects();
  const currentRedirects = site.selectors.getRedirects(state);

  const initialIntegrations = initialData.getIntegrations();
  const currentIntegrations = site.selectors.getIntegrations(state);

  const pagesToCreate = getPagesToCreate(initialPages, currentPages);
  const pagesToUpdate = getPagesToUpdate(initialPages, currentPages);
  const pageIdsToDelete = getPageIdsToDelete(initialPages, currentPages);
  const assetsToUpdate = getAssetsToUpdate(initialPages, currentPages);
  const globalsToUpdate = getAssetsToUpdate(initialGlobals, currentGlobals);
  const assetsToCreate = getAssetsToCreate(initialPages, currentPages);
  const assetSourcesToDelete = getAssetSourcesToDelete(initialPages, currentPages);
  const sitePropertiesToCreate = getSitePropertiesToCreate(initialProperties, currentProperties);
  const sitePropertiesToUpdate = getSitePropertiesToUpdate(initialProperties, currentProperties);
  const customColorPalettesToCreate = getCustomPalettesToCreate(
    initialCustomColorPalettes,
    currentCustomColorPalettes
  );
  const customColorPalettesToDelete = getCustomPalettesToDelete(
    initialCustomColorPalettes,
    currentCustomColorPalettes
  );
  const customColorPalettesToUpdate = getCustomPalettesToUpdate(
    initialCustomColorPalettes,
    currentCustomColorPalettes
  );

  if (currentActivePageId && currentActivePageId !== initialActivePageId) {
    savingActions.push(updateActivePageId(currentActivePageId));
  }

  if (currentSiteName !== initialSiteName) {
    savingActions.push(setSiteName(currentSiteName));
  }

  if (!equal(currentCustomTextColors, initialCustomTextColors)) {
    if (!initialCustomTextColors) {
      savingActions.push(createCustomTextColors(currentCustomTextColors));
    } else {
      savingActions.push(updateCustomTextColors(currentCustomTextColors));
    }
  }

  if (!equal(currentButtonPresetVersion, initialButtonPresetVersion)) {
    if (!initialButtonPresetVersion) {
      savingActions.push(createCurrentButtonPresetVersion(currentButtonPresetVersion));
    } else {
      savingActions.push(
        ...updateCurrentButtonPresetVersion(initialButtonPresetVersion, currentButtonPresetVersion)
      );
    }
  }

  if (!equal(currentColorPaletteVersion, initialColorPaletteVersion)) {
    if (!initialColorPaletteVersion) {
      savingActions.push(createCurrentColorPaletteVersion(currentColorPaletteVersion));
    } else {
      savingActions.push(
        ...updateCurrentColorPaletteVersion(initialColorPaletteVersion, currentColorPaletteVersion)
      );
    }
  }

  if (!equal(currentCustomColors, initialCustomColors)) {
    if (!initialCustomColors) {
      savingActions.push(createCustomColors(currentCustomColors));
    } else {
      savingActions.push(updateCustomColors(currentCustomColors));
    }
  }

  // We need to localize globals sources before save so service would store it's markup into proper locale global
  const globalsWithLocalizedSources = globalsToUpdate.map((global) => {
    if (globals.verifiers.isLocaleUnAwareGlobalSource(global.source)) return global;
    return {
      ...global,
      source: normalizeLocalizedSource(global.source, currentLocale, false),
    };
  });

  savingActions.push(...createPages(pagesToCreate));
  savingActions.push(...updatePages(pagesToUpdate));
  savingActions.push(...deletePages(pageIdsToDelete));
  savingActions.push(...updateAssets(assetsToUpdate));
  savingActions.push(...updateAssets(globalsWithLocalizedSources));
  savingActions.push(...createAssets(assetsToCreate));
  savingActions.push(...deleteAssets(assetSourcesToDelete));
  savingActions.push(...updateAssets(affectedMediaAssets));
  savingActions.push(...createSiteProperties(sitePropertiesToCreate));
  savingActions.push(...updateSiteProperties(sitePropertiesToUpdate));
  savingActions.push(...updateMetaSettings(initialSettings, currentSettings));
  savingActions.push(...createCustomColorPalettes(customColorPalettesToCreate));
  savingActions.push(...updateCustomColorPalettes(customColorPalettesToUpdate));
  savingActions.push(...deleteCustomColorPalettes(customColorPalettesToDelete));
  savingActions.push(...updateCleanUrlEnabled(initialCleanUrlEnabled, currentCleanUrlEnabled));
  savingActions.push(...updateMetaIntegrations(initialIntegrations, currentIntegrations));

  const updateMetaRedirectsAction = updateMetaRedirects(initialRedirects, currentRedirects);
  if (updateMetaRedirectsAction) {
    savingActions.push(updateMetaRedirectsAction);
  }

  return savingActions;
};
